import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  Badge,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Avatar,
  ButtonGroup,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@/contexts/ThemeContext';
import { useTeamService } from '@/hooks/useTeamService';

// Icons
const RequestIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
  </svg>
);

interface MembershipRequest {
  id: string;
  team_id: string;
  user_id: string;
  message?: string;
  status: string;
  created_at: string;
  user?: {
    username: string;
    display_name?: string;
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
  };
}

interface RoleRequest {
  id: string;
  team_id: string;
  user_id: string;
  role_id: string;
  requested_role?: string;
  message?: string;
  status: string;
  created_at: string;
  user?: {
    username: string;
    display_name?: string;
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
  };
}

interface OwnershipRequest {
  id: string;
  team_id: string;
  user_id: string;
  message?: string;
  status: string;
  created_at: string;
  user?: {
    username: string;
    display_name?: string;
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
  };
}

interface TeamRequestsTabProps {
  teamId: string;
  permissions: {
    can_view_team: boolean;
    can_view_membership_requests: boolean;
    can_approve_membership: boolean;
    can_manage_roles: boolean;
  };
  isActive: boolean;
  onApproveMembershipRequest?: (requestId: string, decision: 'approve' | 'reject') => void;
  onApproveRoleRequest?: (requestId: string) => void;
}

export const TeamRequestsTab: React.FC<TeamRequestsTabProps> = ({
  teamId,
  permissions,
  isActive,
  onApproveMembershipRequest,
  onApproveRoleRequest,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { colorSchemes } = useThemeContext();
  const teamService = useTeamService();

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');

  // State
  const [membershipRequests, setMembershipRequests] = useState<MembershipRequest[]>([]);
  const [roleRequests, setRoleRequests] = useState<RoleRequest[]>([]);
  const [ownershipRequests, setOwnershipRequests] = useState<OwnershipRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [hasLoaded, setHasLoaded] = useState(false);

  // Load requests data only when tab becomes active
  const loadRequests = async () => {
    if (!teamId || !permissions.can_view_membership_requests) return;

    try {
      setIsLoading(true);
      setError('');
      
      // Load all request types in parallel
      const [membershipData, roleData, ownershipData] = await Promise.allSettled([
        teamService.getTeamMembershipRequests?.(teamId) || Promise.resolve([]),
        teamService.getTeamRoleRequests?.(teamId) || Promise.resolve([]),
        teamService.getTeamOwnershipRequests?.(teamId) || Promise.resolve([]),
      ]);

      setMembershipRequests(
        membershipData.status === 'fulfilled' ? membershipData.value || [] : []
      );
      setRoleRequests(
        roleData.status === 'fulfilled' ? roleData.value || [] : []
      );
      setOwnershipRequests(
        ownershipData.status === 'fulfilled' ? ownershipData.value || [] : []
      );
      
      setHasLoaded(true);
    } catch (err: any) {
      console.error('Error loading team requests:', err);
      setError(err.message || 'Failed to load team requests');
      toast({
        title: t('common.error'),
        description: err.message || 'Failed to load team requests',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Load data when tab becomes active
  useEffect(() => {
    if (isActive && !hasLoaded && permissions.can_view_membership_requests) {
      loadRequests();
    }
  }, [isActive, hasLoaded, permissions.can_view_membership_requests, teamId]);

  // Get status badge color
  const getStatusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'yellow';
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      case 'cancelled':
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Get user display name
  const getUserDisplayName = (user?: any) => {
    if (!user) return 'Unknown User';
    if (user.display_name) return user.display_name;
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.username || 'Unknown User';
  };

  // Handle approve membership request
  const handleApproveMembershipRequest = async (request: MembershipRequest, decision: 'approve' | 'reject') => {
    if (!permissions.can_approve_membership) return;
    
    try {
      if (onApproveMembershipRequest) {
        await onApproveMembershipRequest(request.id, decision);
        await loadRequests(); // Reload after action
      }
    } catch (error) {
      console.error('Error handling membership request:', error);
    }
  };

  // Handle approve role request
  const handleApproveRoleRequest = async (request: RoleRequest) => {
    if (!permissions.can_manage_roles) return;
    
    try {
      if (onApproveRoleRequest) {
        await onApproveRoleRequest(request.id);
        await loadRequests(); // Reload after action
      }
    } catch (error) {
      console.error('Error handling role request:', error);
    }
  };

  // Permission check
  if (!permissions.can_view_membership_requests) {
    return (
      <Box textAlign="center" py={8}>
        <Alert status="warning">
          <AlertIcon />
          {t('teams.noPermissionToViewRequests')}
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (isLoading && !hasLoaded) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="lg" />
        <Text>{t('teams.loadingRequests')}</Text>
      </VStack>
    );
  }

  // Error state
  if (error && !isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
        <Button mt={4} onClick={loadRequests} colorScheme={colorSchemes.primary}>
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  const totalRequests = membershipRequests.length + roleRequests.length + ownershipRequests.length;

  // Empty state
  if (!isLoading && totalRequests === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text color={textColor} fontSize="lg">
          {t('teams.noRequests')}
        </Text>
        <Text color={textColor} fontSize="sm" mt={2}>
          {t('teams.requestsWillAppearHere')}
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Header */}
      <HStack justify="space-between" align="center">
        <HStack spacing={2}>
          <RequestIcon />
          <Text fontSize="lg" fontWeight="semibold">
            {t('teams.teamRequests')} ({totalRequests})
          </Text>
        </HStack>
        <Button
          size="sm"
          onClick={loadRequests}
          isLoading={isLoading}
          colorScheme={colorSchemes.neutral}
          variant="outline"
        >
          {t('common.refresh')}
        </Button>
      </HStack>

      {/* Requests Tabs */}
      <Tabs colorScheme={colorSchemes.primary}>
        <TabList>
          <Tab>
            <HStack spacing={2}>
              <Text>{t('teams.membershipRequests')}</Text>
              <Badge colorScheme="blue" variant="solid" borderRadius="full">
                {membershipRequests.length}
              </Badge>
            </HStack>
          </Tab>
          <Tab>
            <HStack spacing={2}>
              <Text>{t('teams.roleRequests')}</Text>
              <Badge colorScheme="purple" variant="solid" borderRadius="full">
                {roleRequests.length}
              </Badge>
            </HStack>
          </Tab>
          <Tab>
            <HStack spacing={2}>
              <Text>{t('teams.ownershipRequests')}</Text>
              <Badge colorScheme="orange" variant="solid" borderRadius="full">
                {ownershipRequests.length}
              </Badge>
            </HStack>
          </Tab>
        </TabList>

        <TabPanels>
          {/* Membership Requests */}
          <TabPanel p={0} pt={4}>
            {membershipRequests.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Text color={textColor}>{t('teams.noMembershipRequests')}</Text>
              </Box>
            ) : (
              <Card bg={cardBg} borderWidth={1} borderColor={borderColor}>
                <CardBody p={0}>
                  <TableContainer>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>{t('teams.user')}</Th>
                          <Th>{t('teams.message')}</Th>
                          <Th>{t('teams.status')}</Th>
                          <Th>{t('teams.date')}</Th>
                          <Th>{t('teams.actions')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {membershipRequests.map((request) => (
                          <Tr key={request.id}>
                            <Td>
                              <HStack spacing={3}>
                                <Avatar
                                  size="sm"
                                  name={getUserDisplayName(request.user)}
                                  src={request.user?.avatar_url}
                                />
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {getUserDisplayName(request.user)}
                                  </Text>
                                  {request.user?.username && (
                                    <Text fontSize="xs" color={textColor}>
                                      @{request.user.username}
                                    </Text>
                                  )}
                                </VStack>
                              </HStack>
                            </Td>
                            <Td>
                              <Text fontSize="sm" noOfLines={2} color={textColor}>
                                {request.message || '-'}
                              </Text>
                            </Td>
                            <Td>
                              <Badge
                                colorScheme={getStatusBadgeColor(request.status)}
                                variant="subtle"
                              >
                                {request.status}
                              </Badge>
                            </Td>
                            <Td>
                              <Text fontSize="sm" color={textColor}>
                                {new Date(request.created_at).toLocaleDateString()}
                              </Text>
                            </Td>
                            <Td>
                              {request.status === 'pending' && permissions.can_approve_membership && (
                                <ButtonGroup size="sm">
                                  <Button
                                    colorScheme="green"
                                    variant="outline"
                                    onClick={() => handleApproveMembershipRequest(request, 'approve')}
                                  >
                                    {t('teams.approve')}
                                  </Button>
                                  <Button
                                    colorScheme="red"
                                    variant="outline"
                                    onClick={() => handleApproveMembershipRequest(request, 'reject')}
                                  >
                                    {t('teams.reject')}
                                  </Button>
                                </ButtonGroup>
                              )}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            )}
          </TabPanel>

          {/* Role Requests */}
          <TabPanel p={0} pt={4}>
            {roleRequests.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Text color={textColor}>{t('teams.noRoleRequests')}</Text>
              </Box>
            ) : (
              <Card bg={cardBg} borderWidth={1} borderColor={borderColor}>
                <CardBody p={0}>
                  <TableContainer>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>{t('teams.user')}</Th>
                          <Th>{t('teams.requestedRole')}</Th>
                          <Th>{t('teams.message')}</Th>
                          <Th>{t('teams.status')}</Th>
                          <Th>{t('teams.actions')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {roleRequests.map((request) => (
                          <Tr key={request.id}>
                            <Td>
                              <HStack spacing={3}>
                                <Avatar
                                  size="sm"
                                  name={getUserDisplayName(request.user)}
                                  src={request.user?.avatar_url}
                                />
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {getUserDisplayName(request.user)}
                                  </Text>
                                  {request.user?.username && (
                                    <Text fontSize="xs" color={textColor}>
                                      @{request.user.username}
                                    </Text>
                                  )}
                                </VStack>
                              </HStack>
                            </Td>
                            <Td>
                              <Badge colorScheme="blue" variant="outline">
                                {request.requested_role || request.role_id}
                              </Badge>
                            </Td>
                            <Td>
                              <Text fontSize="sm" noOfLines={2} color={textColor}>
                                {request.message || '-'}
                              </Text>
                            </Td>
                            <Td>
                              <Badge
                                colorScheme={getStatusBadgeColor(request.status)}
                                variant="subtle"
                              >
                                {request.status}
                              </Badge>
                            </Td>
                            <Td>
                              {request.status === 'pending' && permissions.can_manage_roles && (
                                <Button
                                  size="sm"
                                  colorScheme="green"
                                  variant="outline"
                                  onClick={() => handleApproveRoleRequest(request)}
                                >
                                  {t('teams.approve')}
                                </Button>
                              )}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            )}
          </TabPanel>

          {/* Ownership Requests */}
          <TabPanel p={0} pt={4}>
            {ownershipRequests.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Text color={textColor}>{t('teams.noOwnershipRequests')}</Text>
              </Box>
            ) : (
              <Card bg={cardBg} borderWidth={1} borderColor={borderColor}>
                <CardBody p={0}>
                  <TableContainer>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>{t('teams.user')}</Th>
                          <Th>{t('teams.message')}</Th>
                          <Th>{t('teams.status')}</Th>
                          <Th>{t('teams.date')}</Th>
                          <Th>{t('teams.actions')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {ownershipRequests.map((request) => (
                          <Tr key={request.id}>
                            <Td>
                              <HStack spacing={3}>
                                <Avatar
                                  size="sm"
                                  name={getUserDisplayName(request.user)}
                                  src={request.user?.avatar_url}
                                />
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {getUserDisplayName(request.user)}
                                  </Text>
                                  {request.user?.username && (
                                    <Text fontSize="xs" color={textColor}>
                                      @{request.user.username}
                                    </Text>
                                  )}
                                </VStack>
                              </HStack>
                            </Td>
                            <Td>
                              <Text fontSize="sm" noOfLines={2} color={textColor}>
                                {request.message || '-'}
                              </Text>
                            </Td>
                            <Td>
                              <Badge
                                colorScheme={getStatusBadgeColor(request.status)}
                                variant="subtle"
                              >
                                {request.status}
                              </Badge>
                            </Td>
                            <Td>
                              <Text fontSize="sm" color={textColor}>
                                {new Date(request.created_at).toLocaleDateString()}
                              </Text>
                            </Td>
                            <Td>
                              {request.status === 'pending' && (
                                <Text fontSize="sm" color={textColor}>
                                  {t('teams.requiresOwnerApproval')}
                                </Text>
                              )}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  );
}; 